import { ChangeDetectionStrategy, Component, computed, inject } from "@angular/core";
import { SeConnect25ThemeStore } from "./se.connect25.theme.store";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25LoadingInlineModule } from "../s25-loading/s25.loading.inline.module";
import { SeSearchComponent } from "./search/se.search.component";
import { SeSearchResultsComponent } from "./search/tile.search.results.component";
import { SeSearchService } from "./search/se.search.service";
import { PaginatedList } from "./search/paginated.list";
import { DEFAULT_PAGE_SIZE } from "./search/se.search.service";
import { convertEventDocumentToTileItem, convertOrganizationDocumentToTileItem } from "./search/tile.item.converters";
import { s25Resource } from "../../util/s25.resource";
import { StudentEngagementService } from "./student.engagement.service";
import { LoginStore } from "../../stores/login.store";
import { StateService } from "../../services/state.service";
import { S25States } from "../../services/state/state.map";
import { AttachmentService } from "../../services/attachment.service";
import { FileStoreUtil, FILESTORE_IMAGE_EXTENSIONS, resolveImageUrl } from "../../util/filestore.util";
import { S25Util } from "../../util/s25-util";
import { S25SeHeroSlideshowComponent } from "../../standalone/s25.se.hero.slideshow.component";
import { FILE_KEY_CONNECT25_HOME_SLIDESHOW_POOL } from "./se.connect25.constants";
import { SeMyNewsComponent } from "../../standalone/se-my-news/se.my.news.component";
import { Item } from "../../pojo/Item";

@TypeManagerDecorator("s25-ng-se-home")
@Component({
    selector: "s25-ng-se-home",
    template: `
        @if (loading()) {
            <s25-ng-loading-inline-static />
        } @else if (error()) {
            <p>{{ error() }}</p>
        } @else {
            <section
                class="hero-search-section"
                [style.--se-hero-gradient-start]="slideshowGradientStart()"
                [style.--se-hero-gradient-end]="slideshowGradientEnd()"
                [style.background-image]="slideshowSingleBackground()"
            >
                @if (slideshowImageUrls().length > 1) {
                    <s25-se-hero-slideshow [imageUrls]="slideshowImageUrls()" />
                }

                <s25-ng-se-search data-testid="seSearchComponent" #searchComponent></s25-ng-se-search>
            </section>

            <!-- Search Results Section - Shows first when user has searched -->
            @if (searchComponent.hasSearched()) {
                <section class="c-margin-top--single c-margin-bottom--single">
                    <h2>Search Results</h2>
                    <s25-ng-se-search-results
                        [results]="searchComponent.results()"
                        [resultsCount]="searchComponent.resultsCount()"
                        [hasSearched]="searchComponent.hasSearched()"
                        [isSearching]="searchComponent.isSearching()"
                        [searchType]="searchComponent.searchType()"
                        (loadMore)="searchComponent.onLoadMore()"
                    ></s25-ng-se-search-results>
                </section>
            } @else {
                <section class="c-margin-top--single c-margin-bottom--single">
                    <h2>Promoted Events</h2>
                    <s25-ng-se-search-results
                        [paginatedData]="promotedEventsList"
                        [noResultsMessage]="'No promoted events found in the next 4 months. Check back later!'"
                    ></s25-ng-se-search-results>
                    <button class="c-textButton" (click)="navToSearch(evTypeId)">Search All Events</button>
                </section>
                @if (promotedOrgsList.totalCount() > 0) {
                    <section class="c-margin-top--single c-margin-bottom--single">
                        <h2>Promoted Organizations</h2>
                        <div class="organizations-grid">
                            <s25-ng-se-search-results
                                [paginatedData]="promotedOrgsList"
                                [noResultsMessage]="'No organizations found.'"
                                [searchType]="'organizations'"
                            ></s25-ng-se-search-results>
                        </div>
                        <button class="c-textButton" (click)="navToSearch(orgTypeId)">Search All Organizations</button>
                    </section>
                }
                @if (isLoggedIn()) {
                    <section class="c-margin-top--single c-margin-bottom--single">
                        <h2>My News</h2>
                        <s25-ng-se-my-news />
                    </section>
                }
            }
        }
    `,
    styles: `
        .hero-search-section {
            position: relative;
            isolation: isolate;
            aspect-ratio: 3 / 1;
            min-height: 200px;
            max-width: 100%;
            background: linear-gradient(180deg, rgba(15, 23, 41, 0.25) 0%, rgba(15, 23, 41, 0.55) 100%),
                radial-gradient(ellipse at 80% 20%, rgba(31, 68, 134, 0.55), transparent 60%),
                radial-gradient(ellipse at 20% 80%, rgba(255, 172, 28, 0.4), transparent 60%),
                linear-gradient(135deg, #2a3a6e 0%, #5b3d6b 50%, #c97a3a 100%);
            background-size: cover;
            background-position: center;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 2rem;
            border-radius: 4px;
            overflow: hidden;
        }

        h2 {
            font-size: 24px;
            margin-bottom: 20px;
            color: var(--color--text-strong);
            border-bottom: 2px solid var(--color--border);
            padding-bottom: 0.5em;
        }

        s25-ng-loading-inline-static {
            display: block;
            margin: 0 auto;
            width: fit-content;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25LoadingInlineModule,
        SeSearchComponent,
        SeSearchResultsComponent,
        S25SeHeroSlideshowComponent,
        SeMyNewsComponent,
    ],
})
export class SeHomeComponent {
    private readonly themeStore = inject(SeConnect25ThemeStore);
    private loginStore = inject(LoginStore);
    readonly isLoggedIn = computed(() => this.loginStore.loggedIn());

    readonly evTypeId = Item.Ids.Event;
    readonly orgTypeId = Item.Ids.Organization;

    // Paginated lists
    readonly promotedEventsList = new PaginatedList(convertEventDocumentToTileItem, (items) => items);
    readonly promotedOrgsList = new PaginatedList(convertOrganizationDocumentToTileItem, (items) => items);

    private readonly slideshowImages = s25Resource({
        loader: async (): Promise<string[]> => {
            const rawUrls: string[] = [];
            const imageExts = new Set(FILESTORE_IMAGE_EXTENSIONS);

            const [list] = await S25Util.Maybe(
                AttachmentService.getFileList(-1, -1, FILE_KEY_CONNECT25_HOME_SLIDESHOW_POOL),
            );
            if (list?.length) {
                const sorted = [...list].sort((a, b) => {
                    const da = a.createDt ? new Date(a.createDt).getTime() : 0;
                    const db = b.createDt ? new Date(b.createDt).getTime() : 0;
                    return da - db;
                });
                for (const f of sorted) {
                    const ext = FileStoreUtil.getExtensionFromFileName(f.itemName || "").toLowerCase();
                    if (f.itemName && imageExts.has(ext)) {
                        rawUrls.push(
                            FileStoreUtil.buildFileStoreValue(FILE_KEY_CONNECT25_HOME_SLIDESHOW_POOL, f.itemName),
                        );
                    }
                }
            }

            return rawUrls.map((u) => resolveImageUrl(u)).filter((u): u is string => !!u);
        },
    });

    readonly slideshowImageUrls = computed(() => this.slideshowImages.value() ?? []);

    readonly slideshowGradientStart = computed(() => this.themeStore.slideshowGradientStart());

    readonly slideshowGradientEnd = computed(() => this.themeStore.slideshowGradientEnd());

    /** Single static background when not using multi-image slideshow */
    readonly slideshowSingleBackground = computed(() => {
        const slides = this.slideshowImageUrls();
        if (slides.length === 1) return `url(${slides[0]})`;
        return "";
    });

    protected readonly loading = computed(
        () => this.promotedEvents.resource.isLoading() || this.promotedOrgs.resource.isLoading(),
    );
    protected readonly error = computed(
        () => this.promotedEvents.resource.error()?.message || this.promotedOrgs.resource.error()?.message,
    );

    private promotedEvents = s25Resource({
        loader: async () => {
            const response = await SeSearchService.getPromotedEvents(1, 9).catch((error) => {
                this.promotedEventsList.reset();
                throw error;
            });
            this.promotedEventsList.setInitialData(response);
            this.promotedEventsList.configureFetch(SeSearchService.getPromotedEvents, DEFAULT_PAGE_SIZE);
        },
    });

    private promotedOrgs = s25Resource({
        loader: async () => {
            const response = await StudentEngagementService.getPromotedOrgs(1, DEFAULT_PAGE_SIZE).catch((error) => {
                this.promotedOrgsList.reset();
                throw error;
            });
            this.promotedOrgsList.setInitialData(response);
            this.promotedOrgsList.configureFetch(StudentEngagementService.getPromotedOrgs, DEFAULT_PAGE_SIZE);
        },
    });

    navToSearch(itemTypeId: number) {
        const route =
            itemTypeId === Item.Ids.Event ? S25States.names.seEventsSearch : S25States.names.seOrganizationsSearch;
        StateService.go(route, null, null).then(() => window.scrollTo({ top: 0, behavior: "smooth" }));
    }
}
